import { onlineManager, type QueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { redirect, type ShouldRevalidateFunction } from 'react-router-dom'
import { api, queryClient } from '@/lib/api-client.ts'
import { getCurrentUser, userQueryKey } from '@/lib/actions.ts'
import type { CurrStageData } from '@/lib/actions/getCurrStage.ts'
import { useUserStore, type User } from '@/store/user.ts'

// Quick capture is meant for poor signal on site, where the OS reports "online" through a captive
// portal or dead WiFi. Waiting out the 15 s axios default there would freeze the entry, so when a
// cached flag can answer instead, the fresh read gives up early. With nothing cached, giving up
// early would only turn a slow "enabled" into "closed", so that read keeps the default timeout.
export const QUICK_CAPTURE_PROBE_TIMEOUT_MS = 3_000

const STAGE_QUERY_PREFIX = 'currStageUsersQuery'

type StageQueryKey = [
  typeof STAGE_QUERY_PREFIX,
  { projectStageId: string | number; userId: number },
]

function isThisStage(
  queryKey: readonly unknown[],
  projectStageId: string | number,
  userId: number,
) {
  const [, params] = queryKey as StageQueryKey
  return String(params?.projectStageId) === String(projectStageId) && params?.userId === userId
}

/**
 * The per-stage switches the app reads from the stage itself. One GET answers both, and both are
 * fail-closed: only a strict `true` opens anything, so a missing or malformed value reads as off.
 * - `quickCaptureEnabled`: the quick-capture pilot (G6).
 * - `voiceDictationEnabled`: whether this stage offers voice dictation (D2), i.e. whether a
 *   recording made here gets an AI suggestion. Read-only on the server
 *   (ProjectStageVoiceDictationListener fills it from VoiceNoteAiPolicy).
 */
export type StageFlagT = 'quickCaptureEnabled' | 'voiceDictationEnabled'

/**
 * A flag as the stage screen last cached it. Loaders key that query with the route param (a
 * string) and some callers with a number, so both spellings count.
 */
export function cachedStageFlag(
  client: QueryClient,
  flag: StageFlagT,
  projectStageId: string | number,
  userId: number,
): boolean | undefined {
  const entries = client.getQueriesData<CurrStageData>({
    queryKey: [STAGE_QUERY_PREFIX],
    predicate: query => isThisStage(query.queryKey, projectStageId, userId),
  })
  const stage = entries.map(([, data]) => data).find(data => data != null)
  return stage == null ? undefined : stage[flag] === true
}

export function cachedQuickCaptureEnabled(
  client: QueryClient,
  projectStageId: string | number,
  userId: number,
): boolean | undefined {
  return cachedStageFlag(client, 'quickCaptureEnabled', projectStageId, userId)
}

export function cachedVoiceDictationEnabled(
  client: QueryClient,
  projectStageId: string | number,
  userId: number,
): boolean | undefined {
  return cachedStageFlag(client, 'voiceDictationEnabled', projectStageId, userId)
}

function isTransient(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return true
  const status = error.response?.status
  return status == null || status >= 500
}

/**
 * Whether a stage flag is on for this stage and this user.
 *
 * Online it reads the stage fresh, so a second client sees a superadmin's change the next time it
 * enters; offline it answers from the cached stage. It deliberately does NOT go through
 * currStageQuery's queryFn: getCurrStage PATCHes the user's activeProjectStage, and probing a
 * stage must not switch the user to it.
 *
 * Only a network error, a timeout or a 5xx falls back to the cache. A 403 or 404 is an answer
 * (the stage is gone or no longer the user's) and closes the flag even when the cache says open.
 *
 * Every successful read writes back both flags, not only the one asked for: it is the same GET,
 * and the next offline answer for the other flag should not be older than this one.
 */
export async function resolveStageFlag(
  client: QueryClient,
  flag: StageFlagT,
  projectStageId: string | number,
  userId: number,
): Promise<boolean> {
  if (!onlineManager.isOnline()) {
    return cachedStageFlag(client, flag, projectStageId, userId) === true
  }

  const cached = cachedStageFlag(client, flag, projectStageId, userId)
  try {
    const res = await api.get<CurrStageData>(
      `/project_stages/${projectStageId}`,
      cached === undefined ? {} : { timeout: QUICK_CAPTURE_PROBE_TIMEOUT_MS },
    )
    const flags = readStageFlags(res.data)
    writeBackFlags(client, projectStageId, userId, flags)
    return flags[flag]
  } catch (error) {
    if (!isTransient(error)) return false
    // Re-read: a stage refresh may have changed the cache while the probe was pending.
    return cachedStageFlag(client, flag, projectStageId, userId) === true
  }
}

/**
 * Whether quick capture is open on this stage for this user (resolveStageFlag).
 *
 * "Disabling closes the route" means the NEXT entry: an already-open capture screen is not torn
 * down mid-capture (the route must use shouldRevalidateQuickCapture). The flag is a pilot UI
 * switch, not an authorization boundary. Keep it out of server-side write checks, or captures
 * queued offline while it was on would be refused for good once it is turned off (any 4xx is
 * permanent in the offline queue).
 */
export function resolveQuickCaptureEnabled(
  client: QueryClient,
  projectStageId: string | number,
  userId: number,
): Promise<boolean> {
  return resolveStageFlag(client, 'quickCaptureEnabled', projectStageId, userId)
}

/**
 * Whether this stage offers voice dictation to this user (resolveStageFlag). Like quick capture it
 * is a UI switch only: the server checks VoiceNoteAiPolicy again before anything reaches a
 * provider, so a stale `true` on a phone can never send a recording out.
 */
export function resolveVoiceDictationEnabled(
  client: QueryClient,
  projectStageId: string | number,
  userId: number,
): Promise<boolean> {
  return resolveStageFlag(client, 'voiceDictationEnabled', projectStageId, userId)
}

type StageFlagsT = Record<StageFlagT, boolean>

function readStageFlags(stage: CurrStageData | undefined): StageFlagsT {
  return {
    quickCaptureEnabled: stage?.quickCaptureEnabled === true,
    voiceDictationEnabled: stage?.voiceDictationEnabled === true,
  }
}

/**
 * Keep the cached stage in step, so the next offline entry answers with what was just read. Only
 * the flags are fresh, so the entry keeps its fetch time and any pending invalidation: a plain
 * setQueryData would mark the whole stage (name, managers, permissions) as just fetched, letting
 * it skip refetches and outlive the persisted cache's age gate.
 */
function writeBackFlags(
  client: QueryClient,
  projectStageId: string | number,
  userId: number,
  flags: StageFlagsT,
) {
  const queries = client.getQueryCache().findAll({
    queryKey: [STAGE_QUERY_PREFIX],
    predicate: query => isThisStage(query.queryKey, projectStageId, userId),
  })
  for (const query of queries) {
    const { data, dataUpdatedAt, isInvalidated } = query.state
    if (data == null) continue
    client.setQueryData<CurrStageData>(
      query.queryKey,
      { ...(data as CurrStageData), ...flags },
      { updatedAt: dataUpdatedAt },
    )
    if (isInvalidated) {
      void client.invalidateQueries({ queryKey: query.queryKey, exact: true, refetchType: 'none' })
    }
  }
}

/**
 * The current user's id the way loaderCommon finds it. Sibling loaders run in parallel, so the
 * Zustand store may still be empty on a cold load (every first load after a release, since the
 * persisted cache is busted per version).
 */
async function resolveUserId(client: QueryClient): Promise<number | undefined> {
  const cached = client.getQueryData<User>(userQueryKey)?.id ?? useUserStore.getState().user?.id
  if (cached != null) return cached
  if (!onlineManager.isOnline()) return undefined
  try {
    const user = await client.fetchQuery<User, Error>({
      queryKey: userQueryKey,
      queryFn: getCurrentUser,
    })
    return user?.id
  } catch {
    return client.getQueryData<User>(userQueryKey)?.id
  }
}

/**
 * shouldRevalidate for the quick-capture routes. Pair it with requireQuickCapture: without it the
 * router re-runs the guard on every same-stage navigation or form action, and a flag turned off
 * mid-capture would throw the user out of an open capture. Only entering a stage asks again.
 */
export const shouldRevalidateQuickCapture: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  defaultShouldRevalidate,
}) => (currentParams.projectStageId !== nextParams.projectStageId ? defaultShouldRevalidate : false)

type QuickCaptureLoaderArgs = {
  params: {
    projectStageId?: string
  }
}

/**
 * Route-loader guard for the quick-capture screens (G2/G3). Not wrapped in loaderCommon on
 * purpose: loaderCommon returns null when it cannot find a user, and null from this loader means
 * "let the user in". An unknown user or stage redirects instead.
 */
export async function requireQuickCapture({ params }: QuickCaptureLoaderArgs) {
  const projectStageId = params.projectStageId
  const userId = await resolveUserId(queryClient)

  if (
    projectStageId &&
    userId != null &&
    (await resolveQuickCaptureEnabled(queryClient, projectStageId, userId))
  ) {
    return null
  }

  return redirect(`/project_stages/${projectStageId ?? ''}/dashboard`)
}
